import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowRightIcon, CheckIcon, ArrowPathIcon } from '@heroicons/react/24/solid';

const Slider = (props) => {
	const { label = 'Geser untuk Checkout', loadingLabel = 'Memproses...', successLabel = 'Berhasil!', onConfirm, disabled = false, resetAfterMs, handleSize = 44, trackPadding = 0, className = 'bg-white z-40', completeThreshold = 1 } = props;
	const trackRef = useRef(null);
	const handleRef = useRef(null);
	const draggingRef = useRef(false);
	const startXRef = useRef(0);
	const startLeftRef = useRef(0);

	const [dragX, setDragX] = useState(0); // posisi handle (px) dari kiri
	const [maxX, setMaxX] = useState(0); // batas geser maksimum (px)
	const [status, setStatus] = useState('idle'); // idle | dragging | loading | success

	const iconSize = Math.max(16, Math.round(handleSize * 0.42));

	useEffect(() => {
		const track = trackRef.current;
		if (!track) return;

		const updateMaxX = () => {
			setMaxX(track.offsetWidth - handleSize - trackPadding * 2);
		};

		updateMaxX();
		const ro = new ResizeObserver(updateMaxX);
		ro.observe(track);
		return () => ro.disconnect();
	}, [handleSize, trackPadding]);

	const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

	const handlePointerMove = useCallback(
		(clientX) => {
			if (!draggingRef.current) return;
			const delta = clientX - startXRef.current;
			const next = clamp(startLeftRef.current + delta, 0, maxX);
			setDragX(next);
		},
		[maxX],
	);

	const finishDrag = useCallback(async () => {
		if (!draggingRef.current) return;
		draggingRef.current = false;

		const completed = maxX > 0 && dragX >= maxX * completeThreshold;

		if (completed) {
			setDragX(maxX);
			setStatus('loading');
			try {
				await onConfirm?.();
			} catch (err) {
				console.log('err: ', err);
			} finally {
				setStatus('idle');
				setDragX(0);
			}
		} else {
			setStatus('idle');
			setDragX(0);
		}
	}, [dragX, maxX, onConfirm, resetAfterMs, completeThreshold]);

	useEffect(() => {
		const onMouseMove = (e) => handlePointerMove(e.clientX);
		const onTouchMove = (e) => handlePointerMove(e.touches[0].clientX);
		const onUp = () => finishDrag();

		window.addEventListener('mousemove', onMouseMove);
		window.addEventListener('touchmove', onTouchMove, { passive: true });
		window.addEventListener('mouseup', onUp);
		window.addEventListener('touchend', onUp);

		return () => {
			window.removeEventListener('mousemove', onMouseMove);
			window.removeEventListener('touchmove', onTouchMove);
			window.removeEventListener('mouseup', onUp);
			window.removeEventListener('touchend', onUp);
		};
	}, [handlePointerMove, finishDrag]);

	const startDrag = (clientX) => {
		if (disabled || status === 'loading' || status === 'success') return;
		draggingRef.current = true;
		startXRef.current = clientX;
		startLeftRef.current = dragX;
		setStatus('dragging');
	};

	const onMouseDown = (e) => startDrag(e.clientX);
	const onTouchStart = (e) => startDrag(e.touches[0].clientX);

	const progress = maxX > 0 ? dragX / maxX : 0;

	return (
		<div className={`w-full p-4 rounded-md ${className}`}>
			<div
				ref={trackRef}
				className={`relative w-full mt-2 select-none overflow-hidden rounded-md shadow-inner transition-colors !border-none duration-300 ${status === 'success' ? 'bg-[var(--rose-violet)]' : disabled ? 'bg-gray-100' : 'bg-gray-200'}`}
				style={{
					height: `${handleSize + trackPadding * 2}px`,
					padding: `${trackPadding + 5}px`,
				}}>
				<div
					className={`absolute rounded-md transition-colors duration-300 !border-none ${progress > 0 ? 'bg-[var(--rose-violet)]' : 'bg-transparent'}`}
					style={{
						left: `${trackPadding}px`,
						top: `${trackPadding}px`,
						height: `${handleSize}px`,
						width: `${handleSize + dragX}px`,
						transition: draggingRef.current ? 'none' : 'width 200ms ease-out',
					}}
				/>
				<div className='pointer-events-none absolute inset-0 flex items-center justify-center gap-2 font-medium !border-none'>
					<div className='transition-opacity duration-200' style={{ opacity: 1 - progress * 1.3 < 0 ? 0 : 1 - progress * 1.3 }}>
						<div className={disabled ? 'text-gray-400' : 'text-gray-600'}>{status === 'loading' ? loadingLabel : status === 'success' ? successLabel : label}</div>
					</div>
				</div>
				<button
					ref={handleRef}
					type='button'
					aria-label={label}
					role='slider'
					aria-valuemin={0}
					aria-valuemax={100}
					aria-valuenow={Math.round(progress * 100)}
					aria-disabled={disabled}
					disabled={disabled}
					onMouseDown={onMouseDown}
					onTouchStart={onTouchStart}
					className={`absolute box-border m-0 flex appearance-none items-center justify-center rounded-md !border-0 !border-none bg-white p-0 leading-none shadow-md outline-none disabled:cursor-not-allowed ${draggingRef.current ? 'cursor-grabbing' : 'cursor-grab'}`}
					style={{
						left: `${trackPadding}px`,
						top: `${trackPadding}px`,
						width: `${handleSize}px`,
						height: `${handleSize}px`,
						transform: `translateX(${dragX}px)`,
						transition: draggingRef.current ? 'none' : 'transform 200ms ease-out',
						border: '!none',
					}}>
					{status === 'success' ? (
						<CheckIcon style={{ width: iconSize, height: iconSize }} className='text-[var(--rose-violet)]' />
					) : status === 'loading' ? (
						<ArrowPathIcon style={{ width: iconSize, height: iconSize }} className='animate-spin text-[var(--rose-violet)]' />
					) : (
						<ArrowRightIcon style={{ width: iconSize, height: iconSize }} className={disabled ? 'text-gray-400' : 'text-[var(--rose-violet)]'} />
					)}
				</button>
			</div>
		</div>
	);
};

export default Slider;
